import React from 'react';
import { cn } from '@/lib/utils';

/**
 * CoRegulate Card Components
 * 
 * Soft backgrounds, rounded corners, spacious padding
 * Minimal text per card, gentle shadows
 */

/**
 * Base Brand Card
 * Background: Soft Sand (#FFBB98) or Warm Oat (#FBE0C3)
 * Rounded corners: 12-16px
 * Soft shadow, spacious padding - INCREASED for breathing room
 */
export const BrandCard = React.forwardRef(({
  children,
  className,
  variant = 'default', // 'default' | 'elevated' | 'outlined' | 'glass'
  accent = null, // 'lavender' | 'sage' | 'rose' | null
  padding = 'md',
  ...props
}, ref) => {
  // Padding increased ~20% for more breathing room
  const paddingStyles = {
    sm: 'p-5',    // Was p-4
    md: 'p-7',    // Was p-6
    lg: 'p-10',   // Was p-8
    xl: 'p-12',   // Was p-10
  };

  const variantStyles = {
    default: 'glass-card',
    elevated: 'glass-card',
    outlined: 'glass-card border-2',
    sand: 'glass-card-sage',
    glass: 'glass-card',
  };

  const accentStyles = {
    lavender: 'border-l-4 border-l-[#7D8E95]',
    sage: 'border-l-4 border-l-[#7D8E95]',
    rose: 'border-l-4 border-l-[#FFBB98]',
    gold: 'border-l-4 border-l-[#FFBB98]',
    blue: 'border-l-4 border-l-[#7D8E95]',
  };

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl transition-all duration-400 ease-out',
        paddingStyles[padding],
        variantStyles[variant],
        accent && accentStyles[accent],
        'card-hover',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
BrandCard.displayName = 'BrandCard';

/**
 * Selection Card
 * Interactive card for selecting options
 * Gentle hover and selected states - INCREASED padding and text
 */
export const SelectionCard = React.forwardRef(({
  children,
  className,
  selected = false,
  onClick,
  disabled,
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full text-left rounded-2xl p-7',
        'transition-all duration-400 ease-out',
        'text-lg',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7D8E95]/50 focus-visible:ring-offset-2',
        // Use glass-card styling
        selected
          ? 'glass-card-sage border-2 border-[#7D8E95] shadow-[0_4px_16px_rgba(125,142,149,0.12)]'
          : 'glass-card card-hover',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});
SelectionCard.displayName = 'SelectionCard';

/**
 * Action Card
 * Large card for displaying grounding steps
 * Centered content, generous padding - INCREASED for somatic tools
 */
export const ActionCard = React.forwardRef(({
  children,
  className,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-3xl p-12 min-h-[220px]',
        'flex items-center justify-center text-center',
        'glass-card',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
ActionCard.displayName = 'ActionCard';

/**
 * Script Card
 * Card for communication scripts
 * Hoverable with gentle lavender highlight
 */
export const ScriptCard = React.forwardRef(({
  children,
  className,
  onClick,
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-2xl p-6',
        'transition-all duration-400 ease-out',
        'glass-card card-hover',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7D8E95]/50 focus-visible:ring-offset-2',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});
ScriptCard.displayName = 'ScriptCard';

/**
 * Stats Card
 * Card for displaying statistics/metrics
 * Centered, minimal design
 */
export const StatsCard = React.forwardRef(({
  children,
  className,
  label,
  value,
  icon,
  trend,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl p-6 text-center',
        'glass-card',
        className
      )}
      {...props}
    >
      {icon && (
        <div className="mb-3 flex justify-center">
          <div className="w-12 h-12 rounded-xl bg-[rgba(52,70,72,0.12)] flex items-center justify-center text-[#1D6B6B]">
            {icon}
          </div>
        </div>
      )}
      {value !== undefined && (
        <div className="text-2xl font-semibold text-[#1D6B6B] font-heading mb-1">
          {value}
        </div>
      )}
      {label && (
        <div className="text-sm text-[#2A8585]">
          {label}
        </div>
      )}
      {trend && (
        <div className={cn(
          'text-xs mt-2',
          trend > 0 ? 'text-[#7D8E95]' : 'text-[#FFBB98]'
        )}>
          {trend > 0 ? '+' : ''}{trend}%
        </div>
      )}
      {children}
    </div>
  );
});
StatsCard.displayName = 'StatsCard';

/**
 * Shared Action Card
 * Card highlighting shared/co-regulation actions
 */
export const SharedActionCard = React.forwardRef(({
  children,
  className,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl p-6',
        'glass-card-sage',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
SharedActionCard.displayName = 'SharedActionCard';

export default BrandCard;
